/* eslint semi: 1 */
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useMediaListStore, DEFAULT_MAX_ITEMS_COUNT } from '@/pinia/stores/media/mediaListStore';
import { useMediaSelectionStore } from '@/pinia/stores/media/mediaSelectionStore';
import { MediaResourceItem, useMediaStore } from '@/pinia/stores/media/mediaStore';
import { useMediaQuery, type LEDTagDimension } from '@/composables/media/use-media-query';
import CONST from '@/globals/constants';
import EventBus from '@/components/mixins/event-bus-mixin';
import type ApiError from '@/globals/api-error';
import { useLocalStorageStore, LIST_VIEW_CONTEXT_MEDIA, type ListViewMode } from '@base-shared/stores/local-storage';

/**
 * Current max width of the media list container.
 * This will be used to calculate the maximum width of the
 * media card item in grid view for LED and Videowall pages
 * to determine minimum width of media list preview items
 * for media list queries.
 */
const DEFAULT_MAX_CONTAINER_WIDTH = 1280;

export function useMediaListPage () {
  const $apiError = inject('$apiError') as typeof ApiError;
  const store = useMediaListStore();
  const mediaQuery = useMediaQuery();
  const mediaSelectionStore = useMediaSelectionStore();
  const mediaItemStore = useMediaStore();
  const localStorageStore = useLocalStorageStore();
  const { t } = useI18n();

  /** All media items for each page context refs to be populated */
  const filesAllItems = ref<MediaResourceItem[]>([]);
  const alertAllItems = ref<MediaResourceItem[]>([]);
  const ledAllItems = ref<MediaResourceItem[]>([]);
  const videowallAllItems = ref<MediaResourceItem[]>([]);

  /** Indicates whether all items are being populated */
  const isPopulatingAllItems = ref(false);
  const isPopulatingAlertsPageItems = ref(false);
  const isPopulatingFilesPageItems = ref(false);
  const isPopulatingLedPageItems = ref(false);
  const isPopulatingVideowallPageItems = ref(false);

  /** State for Media pages list view mode */
  const listViewMode = computed<ListViewMode>(() => {
    return localStorageStore.listViewModePreferences?.[LIST_VIEW_CONTEXT_MEDIA] ?? 'grid';
  });

  const pageIndex = computed(() => store.pageIndex);
  const pageStartIndex = computed(() => pageIndex.value * DEFAULT_MAX_ITEMS_COUNT);
  const pageEndIndex = computed(() => (pageIndex.value + 1) * DEFAULT_MAX_ITEMS_COUNT);
  const isFetching = computed(() => {
    if (
      isPopulatingAllItems.value ||
      store.scheduledFilterMedia?.isFetching
    ) {
      return true;
    }

    if (mediaQuery.isFilesPage.value) return store.isFetchingFilesPage;
    if (mediaQuery.isAlertPage.value) return store.isFetchingAlertsPage;
    if (mediaQuery.isLedPage.value) return store.isFetchingLeds;
    if (mediaQuery.isVideowallPage.value) return store.isFetchingVideowall;

    return false;
  });
  const error = computed(() => {
    if (store.scheduledFilterMedia?.error) return store.scheduledFilterMedia.error;
    if (mediaQuery.isFilesPage.value) return store.filesPageError;
    if (mediaQuery.isAlertPage.value) return store.alertsPageError;
    if (mediaQuery.isLedPage.value) return store.ledPageError;
    if (mediaQuery.isVideowallPage.value) return store.videowallPageError;

    return null;
  });

  /** Files page specific media items */
  const filesPageItems = computed(() => filesAllItems.value.slice(pageStartIndex.value, pageEndIndex.value));
  /** Alert page specific media items */
  const alertPageItems = computed(() => alertAllItems.value.slice(pageStartIndex.value, pageEndIndex.value));
  /** LED page specific media items */
  const ledPageItems = computed(() => ledAllItems.value.slice(pageStartIndex.value, pageEndIndex.value));
  /** Videowall page specific media items */
  const videowallPageItems = computed(() => videowallAllItems.value.slice(pageStartIndex.value, pageEndIndex.value));

  /** Page context computed variables for Media List page */
  const allItems = computed(() => {
    if (mediaQuery.isFilesPage.value) return filesAllItems.value;
    if (mediaQuery.isAlertPage.value) return alertAllItems.value;
    if (mediaQuery.isLedPage.value) return ledAllItems.value;
    if (mediaQuery.isVideowallPage.value) return videowallAllItems.value;

    return [];
  });

  const pageItems = computed(() => {
    if (mediaQuery.isFilesPage.value) return filesPageItems.value;
    if (mediaQuery.isAlertPage.value) return alertPageItems.value;
    if (mediaQuery.isLedPage.value) return ledPageItems.value;
    if (mediaQuery.isVideowallPage.value) return videowallPageItems.value;

    return [];
  });

  /**
   * The final list of media items to display on the
   * current page. This overlays any staged updates
   * onto the page items.
   */
  const items = computed(() => {
    if (store.stagedMediaUpdates?.size && pageItems.value.length) {
      // Overlay staged updates onto the displayed items without
      // mutating the canonical list.
      return pageItems.value.map(item => {
        const update = store.stagedMediaUpdates.get(item.resourceId);
        return update?.resourceId && update.resourceId === item.resourceId
          ? update
          : item;
      });
    }

    return pageItems.value;
  });

  /** Total count of pages loaded */
  const totalPagesLoaded = computed(() => {
    const media = allItems.value ?? [];

    return media.length > 0
      ? Math.ceil(media.length / DEFAULT_MAX_ITEMS_COUNT)
      : 0;
  });
  /** Total count of ALL items */
  const totalItems = computed(() => allItems.value?.length ?? 0);
  const hasNextPage = computed(() => pageIndex.value < totalPagesLoaded.value - 1);
  const hasPreviousPage = computed(() => pageIndex.value > 0);
  const paginatorCounter = computed(() => {
    // Get current page
    const currentPage = pageIndex.value + 1;
    // Get previous pages items count based on the
    // current page and max items per page count
    const previousPagesItemsCount = currentPage > 1
      ? (currentPage - 1) * DEFAULT_MAX_ITEMS_COUNT
      : 0;

    // Get current page items from batch history
    const currentPageItemsCount = items.value?.length ?? 0;

    // Get minimum and maximum items count for the current page
    const minItemsCount = currentPage > 1
      ? previousPagesItemsCount + 1
      : currentPageItemsCount > 0 ? 1 : 0;
    const maxItemsCount = currentPage > 1
      ? previousPagesItemsCount + currentPageItemsCount
      : currentPageItemsCount;

    // Get total items count for all pages
    const totalItemsCount = totalItems.value;

    return {
      currentPage,
      minItemsCount,
      maxItemsCount,
      totalItemsCount
    };
  });

  /**
   * Watcher for mediaListStore state changes (e.g. update, delete, prepend)
   * to re-populate the all items refs for each page context based.
   */
  watch(() => store.filesPageMedia, async () => {
    if (!mediaQuery.isFilesPage.value || isPopulatingFilesPageItems.value) return;
    await populateFilesPageItems();
  }, {
    deep: true
  });
  watch(() => store.alertsPageMedia, async () => {
    if (!mediaQuery.isAlertPage.value || isPopulatingAlertsPageItems.value) return;
    await populateAlertsPageItems();
  }, {
    deep: true
  });
  watch(() => store.ledMediaCanvasMap, async () => {
    if (!mediaQuery.isLedPage.value || isPopulatingLedPageItems.value) return;
    await populateLedPageItems();
  }, {
    deep: true
  });
  watch(() => store.videowallMediaCanvasMap, async () => {
    if (!mediaQuery.isVideowallPage.value || isPopulatingVideowallPageItems.value) return;
    await populateVideowallPageItems();
  }, {
    deep: true
  });

  /**
   * Watcher for pageIndex and totalPagesLoaded to ensure
   * that the pageIndex is always within the valid range of
   * loaded pages.
   */
  watch([pageIndex, totalPagesLoaded], () => {
    if (totalPagesLoaded.value === 0) {
      // No pages available yet — keep pageIndex at 0 (page 1)
      if (pageIndex.value !== 0) store.setPageIndex(0);
      return;
    }

    // Clamp the pageIndex to the valid range of loaded pages
    const lastPage = totalPagesLoaded.value - 1;
    const clamped = Math.max(0, Math.min(pageIndex.value, lastPage));
    if (pageIndex.value !== clamped) {
      store.setPageIndex(clamped);
    }
  });

  /**
   * Toggles the media list view mode between 'grid' and 'table' based on user selection.
   * @param {string} mode - the list view mode to switch to, either 'grid' or 'table'
   */
  function toggleListViewMode (mode: ListViewMode) {
    localStorageStore.setListViewModePreferenceForContext(LIST_VIEW_CONTEXT_MEDIA, mode);
  }

  /**
   * Navigates to the next page, fetching from the API if not yet cached.
   */
  async function nextPage () {
    if (!hasNextPage.value) return;
    store.setPageIndex(store.pageIndex + 1);
  }

  /**
   * Navigates to the previous page. Previous pages are always cached, no fetch needed.
   */
  async function previousPage () {
    if (!hasPreviousPage.value) return;
    store.setPageIndex(store.pageIndex - 1);
  }

  /**
   * Navigates directly to any page by index, fetching if not yet cached.
   * Note: sequential loading is required — skipping ahead to a page that
   * requires an unknown nextToken is not supported.
   */
  async function goToPage (index: number) {
    if (index < 0) return;
    store.setPageIndex(index);
  }

  async function handleErrorNotification () {
    const pageError = error.value;
    const isPageFetching = isFetching.value;
    if (isPageFetching || !pageError) {
      return;
    }

    if (pageError?.response?.status) {
      $apiError.handleAPIRequestError(pageError, {
        showDialogNotification: true
      });
    } else {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('playerSetup.UnknownError')
        },
        callbacks: {
          eventOnClose: () => {}
        }
      });
    }
  }

  /**
   * Determines whether a refetch of the media list is needed based on the last fetched timestamp
   * and an optional force flag.
   * @param lastFetchedAt ISO string of the last fetched timestamp, or null if never fetched
   * @param force If true, forces a refetch regardless of the timestamp
   * @returns true if a refetch should be performed, false otherwise
   */
  function shouldPerformRefetch (lastFetchedAt: string | null, force?: boolean) {
    if (force) return true;
    if (!lastFetchedAt) return true;

    const lastFetched = DateTime.fromISO(lastFetchedAt);
    if (!lastFetched.isValid) return true;

    return lastFetched.plus({ hours: 1 }) <= DateTime.now();
  }

  /**
   * Gets the list of media IDs to filter by based on the
   * scheduled filter state in the store and the current
   * page context.
   * @param pageContext - the page context to check against
   * @returns an array of media IDs to filter by if the scheduled filter is active and matches the page context, or undefined otherwise
   */
  function getFilterByIsScheduled (pageContext: string): string[] | undefined {
    if (typeof mediaQuery.isScheduled.value !== 'boolean') return undefined;

    // Get the expected LED dimension based on the page context and media query state.
    // undefined if not applicable (e.g., Files or Alerts page), otherwise the selected LED dimension.
    const expectedLedDimension = (pageContext === CONST.MEDIA_COMPONENT_AS_LED_FILES || pageContext === CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES)
      ? (mediaQuery.selectedLedTag.value?.dimension ?? undefined)
      : undefined;

    return store.scheduledFilterMedia?.pageContext === pageContext && store.scheduledFilterMedia.ledDimension === expectedLedDimension
      ? store.scheduledFilterMedia.mediaItems?.map(item => item.resourceId) ?? []
      : undefined;
  }

  /**
   * Populates the file page items ref with the filtered
   * and sorted list of media items from the store.
   */
  async function populateFilesPageItems () {
    isPopulatingFilesPageItems.value = true;
    filesAllItems.value = await mediaQuery.filterAndSortList(store.filesPageMedia, {
      filterByIds: getFilterByIsScheduled(CONST.MEDIA_COMPONENT_AS_MEDIA_FILES)
    });
    isPopulatingFilesPageItems.value = false;
  }

  /**
   * Populates the alert page items ref with the filtered
   * and sorted list of media items from the store.
   */
  async function populateAlertsPageItems () {
    isPopulatingAlertsPageItems.value = true;
    alertAllItems.value = await mediaQuery.filterAndSortList(store.alertsPageMedia, {
      filterByIds: getFilterByIsScheduled(CONST.MEDIA_COMPONENT_AS_ALERT_FILES)
    });
    isPopulatingAlertsPageItems.value = false;
  }

  /**
   * Populates the LED page items ref with the filtered
   * and sorted list of media items from the store.
   */
  async function populateLedPageItems () {
    isPopulatingLedPageItems.value = true;
    const selectedLedDimension = mediaQuery.selectedLedTag.value?.dimension;
    const ledMedia = selectedLedDimension
      ? store.ledMediaCanvasMap.get(selectedLedDimension)?.media ?? []
      : [];

    ledAllItems.value = await mediaQuery.filterAndSortList(ledMedia, {
      filterByIds: getFilterByIsScheduled(CONST.MEDIA_COMPONENT_AS_LED_FILES)
    });
    isPopulatingLedPageItems.value = false;
  }

  /**
   * Populates the Videowall page items ref with the filtered
   * and sorted list of media items from the store.
   */
  async function populateVideowallPageItems () {
    isPopulatingVideowallPageItems.value = true;
    const selectedLedDimension = mediaQuery.selectedLedTag.value?.dimension;
    const videowallMedia = selectedLedDimension
      ? store.videowallMediaCanvasMap.get(selectedLedDimension)?.media ?? []
      : [];

    videowallAllItems.value = await mediaQuery.filterAndSortList(videowallMedia, {
      filterByIds: getFilterByIsScheduled(CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES)
    });
    isPopulatingVideowallPageItems.value = false;
  }

  /**
   * Populates the all items refs for each page context
   * based on the current media query state and store data.
   * This should be called after the initial media list is
   * loaded to prepare the full list of items for pagination
   * and filtering.
   */
  async function populateAllItems () {
    isPopulatingAllItems.value = true;

    if (mediaQuery.isFilesPage.value) {
      await populateFilesPageItems();
    } else if (mediaQuery.isAlertPage.value) {
      await populateAlertsPageItems();
    } else if (mediaQuery.isLedPage.value) {
      await populateLedPageItems();
    } else if (mediaQuery.isVideowallPage.value) {
      await populateVideowallPageItems();
    }

    isPopulatingAllItems.value = false;
  }

  /**
   * Initializes the scheduled filter based on the current
   * page context and media query state.
   * - If the scheduled filter is active and the current
   * scheduled filter in the store does not match the
   * active filter, fetch the scheduled filter media and
   * update the store.
   * - If the scheduled filter is not active, reset the
   * scheduled filter in the store to clear any applied
   * filter and cached data.
   */
  async function initializeScheduledFilter () {
    // Skip fetching if isScheduled is not a boolean, meaning
    // the scheduled filter is not active.
    if (typeof mediaQuery.isScheduled.value !== 'boolean') {
      store.resetScheduledFilter();
      return;
    }

    const isScheduled = mediaQuery.isScheduled.value;
    const pageContext = mediaQuery.pageContext.value;
    const ledDimension = mediaQuery.isLedPage.value || mediaQuery.isVideowallPage.value
      ? mediaQuery.selectedLedTag.value?.dimension ?? undefined
      : undefined;

    if (store.scheduledFilterMedia?.pageContext === pageContext &&
        store.scheduledFilterMedia?.value === isScheduled &&
        store.scheduledFilterMedia?.ledDimension === ledDimension) {
      // If the current scheduled filter in the store matches the active filter, we can skip fetching.
      return;
    }

    await store.loadScheduledFilter(isScheduled, pageContext, ledDimension);
    handleErrorNotification();
  }

  /**
   * Initialize the media list for FILES page specific use
   * case (fetching the full list with client-side pagination)
   * @param force If true, forces reloading the first page even if it's already loaded.
   */
  async function initializeFilesPage (force?: boolean) {
    // Skip fetching if we have fetched within the last
    // hour and there is cached data, unless forced.
    if (!shouldPerformRefetch(store.filesPageLastFetchedAt, force)) return;

    await store.loadFilesPage();
    handleErrorNotification();
  }

  /**
   * Initialize the media list for ALERTS page specific use
   * case (fetching the full list with client-side pagination)
   * @param force If true, forces reloading the first page even if it's already loaded.
   */
  async function initializeAlertsPage (force?: boolean) {
    // Skip fetching if we have fetched within the last
    // hour and there is cached data, unless forced.
    if (!shouldPerformRefetch(store.alertsPageLastFetchedAt, force)) return;

    await store.loadAlertsPage();
    handleErrorNotification();
  }

  /**
   * Initializes the media list for LED page specific use
   * case (fetching by LED dimension with client-side pagination)
   * @param force If true, forces reloading the first page even if it's already loaded.
   */
  async function initializeLEDPage (force?: boolean) {
    const selectedLED = mediaQuery.selectedLedTag.value.dimension;
    // Calculate the layout for the selected LED tag to
    // determine the preview width for media items.
    const layout = calculateLedCardLayout(DEFAULT_MAX_CONTAINER_WIDTH, mediaQuery.selectedLedTag.value);
    const previewWidth = layout?.cardWidth
      ? Math.floor(layout.cardWidth)
      : undefined;

    // If no media or the selected dimension media is not
    // loaded yet, we need to load the selected OR default
    // dimension.
    if (
      store.ledMediaCanvasMap.size === 0 ||
      !store.ledMediaCanvasMap.has(selectedLED)
    ) {
      await store.loadLedDimension(selectedLED, previewWidth);
      handleErrorNotification();
      return;
    }

    const cachedMediaCanvas = selectedLED ? store.ledMediaCanvasMap.get(selectedLED) : null;
    // Skip fetching if we have fetched within the last
    // hour and there is cached data, unless forced.
    if (!shouldPerformRefetch(cachedMediaCanvas?.lastFetchedAt ?? null, force)) return;

    if (force) {
      // Reload all if forced, meaning user refreshed manually
      await store.reloadAccumulatedLeds();
    } else {
      // Otherwise, we load the selected dimension to update
      // the cache.
      await store.loadLedDimension(selectedLED, previewWidth);
    }

    handleErrorNotification();
  }

  async function initializeVideowallPage (force?: boolean) {
    const selectedLED = mediaQuery.selectedLedTag.value.dimension;
    // Calculate the layout for the selected LED tag to
    // determine the preview width for media items.
    const layout = calculateLedCardLayout(DEFAULT_MAX_CONTAINER_WIDTH, mediaQuery.selectedLedTag.value);
    const previewWidth = layout?.cardWidth
      ? Math.floor(layout.cardWidth)
      : undefined;

    // If no media or the selected dimension media is not
    // loaded yet, we need to load the selected OR default
    // dimension.
    if (
      store.videowallMediaCanvasMap.size === 0 ||
      !store.videowallMediaCanvasMap.has(selectedLED)
    ) {
      await store.loadVideowallDimension(selectedLED, previewWidth);
      handleErrorNotification();
      return;
    }

    const cachedMediaCanvas = selectedLED ? store.videowallMediaCanvasMap.get(selectedLED) : null;
    // Skip fetching if we have fetched within the last
    // hour and there is cached data, unless forced.
    if (!shouldPerformRefetch(cachedMediaCanvas?.lastFetchedAt ?? null, force)) return;

    if (force) {
      // Reload all if forced, meaning user refreshed manually
      await store.reloadAccumulatedVideowall();
    } else {
      // Otherwise, we load the selected dimension to update
      // the cache.
      await store.loadVideowallDimension(selectedLED, previewWidth);
    }

    handleErrorNotification();
  }

  /**
   * Resets the media list and loads the first page with current filter params.
   * @param force If true, forces reloading the first page even if it's already loaded.
   */
  async function initialize (force?: boolean) {
    if (isFetching.value) return;

    mediaSelectionStore.reset();
    mediaItemStore.reset();
    mediaQuery.initialize();

    // Fetch the scheduled filter first if it's active, or
    // skip if not. This ensures that the media list is
    // loaded with the correct filter applied.
    await initializeScheduledFilter();

    if (mediaQuery.isFilesPage.value) {
      await initializeFilesPage(force);
    } else if (mediaQuery.isAlertPage.value) {
      await initializeAlertsPage(force);
    } else if (mediaQuery.isLedPage.value) {
      await initializeLEDPage(force);
    } else if (mediaQuery.isVideowallPage.value) {
      await initializeVideowallPage(force);
    }

    // After initialization, populate the all items refs for each
    // page context to be used for pagination and filtering.
    await populateAllItems();
  }

  /**
   * Handles the event when a LED tag is removed.
   * @param dimension - the dimension of the LED tag to remove
   * @param type - the type of the LED tag ('led' or 'videowall')
   */
  function handleLedTagRemove (dimension: string, type: 'led' | 'videowall') {
    store.removeLedDimension(dimension, type);
  }

  /**
   * Handles the event when new media files are uploaded
   * and in processing state from {@link FileUploader} with
   * `processNewFilesWithInstance` event to fetch the full
   * details of the new media items to be rendered in the
   * media list.
   * @param resourceIds - array of resource IDs for the new media items
   */
  async function handleUploadProcessNewFilesEvent (resourceIds: string[]) {
    if (!resourceIds.length) return;
    const results = await Promise.all(resourceIds.map(id => mediaItemStore.fetchDetails(id)));

    results
      .map((result) => (result?.response?.data ?? null))
      .filter(Boolean)
      .forEach((media) => store.prependItem(media));
  }

  /**
   * Cancels any in-flight fetchAccumulatedPages loop. Call this from
   * onUnmounted to prevent stale requests from racing with a new mount.
   */
  function cancelFetch () {
    store.cancelFetch();
  }

  /**
   * Resets the media list store to clear all loaded pages
   * and cached data.
   */
  function resetListStore () {
    store.reset();
  }

  /**
   * Resets the scheduled filter in the store to clear any applied
   * filter and cached data related to the scheduled filter.
   * Call this when the scheduled filter is toggled off or when
   * the page context changes to ensure the media list is not
   * filtered by an inactive scheduled filter.
   */
  function resetScheduledFilter () {
    store.resetScheduledFilter();
  }

  function mergeStagedMediaUpdates () {
    store.mergeStagedMediaUpdates();
  }

  onMounted(() => {
    // Initialize the list view mode from local storage on mount.
    localStorageStore.getListViewModePreferenceForContext(LIST_VIEW_CONTEXT_MEDIA);
  });

  return {
    items,
    isFetching,
    error,
    pageIndex,
    totalPagesLoaded,
    hasNextPage,
    hasPreviousPage,
    paginatorCounter,
    listViewMode,

    initialize,
    toggleListViewMode,
    nextPage,
    previousPage,
    goToPage,
    cancelFetch,
    handleLedTagRemove,
    handleUploadProcessNewFilesEvent,
    resetListStore,
    resetScheduledFilter,
    mergeStagedMediaUpdates
  };
}

/**
* Calculates the layout for LED media cards based on the
* container width and LED tag dimensions.
*
* Notes about the current implementation:
* - Aspect-ratio breakpoints use fixed thresholds (5, 2, 4/3, 3/4, 1/2)
*   with direct >= comparisons to choose itemsPerRow.
* - cardWidth and cardHeight are integer pixel values because they are
*   rounded down with Math.floor().
* - Gutter is clamped to >= 0 to guard against tiny floating point
*   residues in remaining width calculations.
*
* @param containerWidth - width of the container in pixels
* @param ledTag - LED tag dimensions and metadata
* @param cardMargin - optional margin budget per column in pixels (default: 0)
* @returns calculated card dimensions and row layout, or null for invalid input
*/
export function calculateLedCardLayout (
  containerWidth: number,
  ledTag: LEDTagDimension,
  cardMargin?: number
) {
  if (!ledTag || containerWidth <= 0) {
    return null;
  }

  const { width: srcWidth, height: srcHeight } = ledTag;

  // Prevent divisions by zero and propagation of NaN/Infinity.
  if (srcWidth <= 0 || srcHeight <= 0) {
    return null;
  }

  const ar = srcWidth / srcHeight;

  let itemsPerRow: number;

  // Choose columns by aspect-ratio bucket.
  if (ar >= 5) itemsPerRow = 1;
  else if (ar >= 2) itemsPerRow = 2;
  else if (ar >= 4 / 3) itemsPerRow = 3;
  else if (ar >= 3 / 4) itemsPerRow = 4;
  else if (ar >= 1 / 2) itemsPerRow = 5;
  else itemsPerRow = 6;

  // Margin budget reserved per column.
  const previewMargin = cardMargin ?? 0;
  const totalMargins = previewMargin * (itemsPerRow - 1);

  const maxWidth = (containerWidth - totalMargins) / itemsPerRow;

  // Portrait content is effectively height-uncapped so that
  // very tall assets can scale based on width alone.
  const maxHeight = srcHeight > srcWidth
    ? 20_000
    : maxWidth;

  const scale = Math.min(
    maxWidth / srcWidth,
    maxHeight / srcHeight
  );

  const cardWidth = Math.floor(srcWidth * scale);
  const cardHeight = Math.floor(srcHeight * scale);

  // Floating point math can leave tiny negative residues when
  // remaining space should mathematically be zero.
  const remainingWidth = containerWidth - itemsPerRow * cardWidth;

  const gutter =
    itemsPerRow > 1
      ? Math.max(
        0,
        Math.floor(
          remainingWidth / (itemsPerRow - 1)
        )
      )
      : 0;

  return {
    cardWidth,
    cardHeight,
    itemsPerRow,
    gutter
  };
}
